import React from "react";
import { formatDateOnly, isSameDay } from "@/utils/dateUtils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Calendar as CalendarIcon,
  ArrowUpRight,
  AlertCircle,
  Clock,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  useUpcomingReportingDatesController,
  UpcomingReportingDatesProps,
} from "./useUpcomingReportingDatesController";

export function UpcomingReportingDates(props: UpcomingReportingDatesProps) {
  const controller = useUpcomingReportingDatesController(props);

  if (!controller.selectedReportingPeriod) {
    return (
      <Card className="mt-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-md flex items-center">
            <CalendarIcon className="h-4 w-4 mr-2" />
            Upcoming Reporting Periods
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center text-amber-500 text-sm">
            <AlertCircle className="h-4 w-4 mr-2" />
            Select a reporting period to see upcoming dates
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-md flex items-center justify-between">
          <div className="flex items-center">
            <CalendarIcon className="h-4 w-4 mr-2" />
            Upcoming Reporting Periods
          </div>
          <Badge variant="outline" className="text-xs">
            Based on {controller.calendar.intervalDefinition}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {controller.baseDate && (
          <div className="mb-3 border-b pb-2">
            <div className="text-xs text-gray-500 mb-1">
              Current period end date:
            </div>
            <div className="flex items-center text-sm">
              <Clock className="h-3 w-3 mr-2 text-primary" />
              <span className="font-medium">
                {formatDateOnly(controller.baseDate.toISOString())}
              </span>
            </div>
          </div>
        )}

        {controller.upcomingDates.length > 0 ? (
          <ul className="space-y-1">
            {controller.upcomingDates.map((entry, index) => (
              <li
                key={index}
                className={`flex justify-between py-1 border-b last:border-0 text-sm ${
                  entry.isNextPeriod ? "bg-primary/5 -mx-2 px-2 rounded" : ""
                }`}
              >
                <span className="font-medium flex items-center">
                  {formatDateOnly(entry.date.toISOString())}
                  {entry.isNextPeriod && (
                    <Badge variant="secondary" className="ml-2 text-xs">
                      Next
                    </Badge>
                  )}
                </span>
                <span className="text-gray-500 text-xs flex items-center">
                  {entry.daysUntil === 0
                    ? "Today"
                    : entry.daysUntil < 0
                    ? `${Math.abs(entry.daysUntil)} days ago`
                    : `in ${entry.daysUntil} days`}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500 text-sm">
            No upcoming dates could be calculated
          </p>
        )}
      </CardContent>
    </Card>
  );
}
